import React from "react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavbarProps {
    errorLeft: boolean;
    errorRight: boolean;
    bucketPathInput: string;
    bucketPathInput2: string;
    FONT_FAMILY: string;
    COLORS: any;
    hideForm?: boolean;
    isMobileDevice?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
    errorLeft,
    errorRight,
    bucketPathInput,
    bucketPathInput2,
    FONT_FAMILY,
    COLORS,
    hideForm = false,
    isMobileDevice = false,
}) => {
    const location = useLocation();
    // Ensure there is exactly one slash between base and '/app'
    const base = (process.env.REACT_APP_APP_SERVER || (typeof window !== 'undefined' ? window.location.origin + '/' : 'http://localhost:3000/'));
    let url = base.replace(/\/+$/, '') + '/app';
    const params = [];
    if (!errorLeft && bucketPathInput) params.push(`bucketPath=${encodeURIComponent(bucketPathInput)}`);
    if (!errorRight && bucketPathInput2) params.push(`bucketPath2=${encodeURIComponent(bucketPathInput2)}`);
    if (params.length > 0) url += '?' + params.join('&');
    // Use full URL - let CSS handle visual overflow
    const displayUrl = url;
    // Copy handler with fallback
    const [isCopying, setIsCopying] = useState(false);

    // Check if anonymized mode is enabled
    const isAnonMode = process.env.REACT_APP_ANON_MODE === 'true' || process.env.ANON_MODE === 'true';
    console.log('Navbar isAnonMode:', isAnonMode, 'REACT_APP_ANON_MODE:', process.env.REACT_APP_ANON_MODE, 'ANON_MODE:', process.env.ANON_MODE);

    const handleCopy = () => {
        setIsCopying(true);
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url);
        } else {
            // fallback for older browsers
            const textarea = document.createElement('textarea');
            textarea.value = url;
            document.body.appendChild(textarea);
            textarea.select();
            try {
                document.execCommand('copy');
            } catch (err) { }
            document.body.removeChild(textarea);
        }
    };



    return (
        <div
            style={{
                width: "100vw",
                position: "fixed",
                top: 0,
                left: 0,
                zIndex: 300,
                background: COLORS.background,
                color: COLORS.text,
                padding: isMobileDevice ? "16px 0 16px 20px" : "20px 0 20px 20px",
                margin: "0 0 0 0px",
                textAlign: "left",
                fontFamily: FONT_FAMILY,
                boxShadow: COLORS.boxShadow,
            }}
        >
            {/* Group logo and subtitle together, with subtitle below logo */}
            <div style={{ display: "flex", alignItems: "flex-start" }}>
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: isMobileDevice ? 8 : 6
                }}>
                    <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                        <div style={{ display: "flex", alignItems: "center", fontSize: 32, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, fontFamily: FONT_FAMILY, cursor: 'pointer' }}>
                            <img
                                src="/logo_arcade.png"
                                alt="arcade logo"
                                style={{ height: 40, width: 40, marginRight: 8, objectFit: "contain" }}
                            />
                            arcade
                        </div>
                    </Link>
                    <div style={{
                        fontSize: isMobileDevice ? 14 : 18,
                        fontWeight: 400,
                        color: COLORS.accent,
                        letterSpacing: 0.5,
                        fontFamily: FONT_FAMILY,
                        marginLeft: 2,
                        lineHeight: isMobileDevice ? 1.2 : 1.4,
                        marginBottom: isMobileDevice ? 12 : 0
                    }}>
                        Surface Flux Transport (SFT) with learnable AI parameters
                    </div>
                </div>
            </div>

            {/* Right-side container for copyable URL and logos - hide logos on mobile */}

            <div style={{ position: 'absolute', right: 30, top: 24, display: 'flex', alignItems: 'center', gap: '16px', zIndex: 400 }}>
                {/* NASA logo with PARTNER label - hidden on mobile */}
                {!isAnonMode && !isMobileDevice && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                        <a
                            href="https://www.nasa.gov/"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <img
                                src="/logos/logo-nasa.png"
                                alt="NASA"
                                style={{
                                    height: 44,
                                    width: 'auto',
                                    objectFit: "contain",
                                    filter: 'brightness(0.9)',
                                    transition: 'filter 0.2s ease'
                                }}
                            />
                        </a>
                        <div style={{
                            background: '#fff',
                            color: '#000',
                            fontSize: 5,
                            fontWeight: 'bold',
                            padding: '2px 4px',
                            borderRadius: '2px',
                            textAlign: 'center',
                            minWidth: '40px',
                            letterSpacing: '0.5px'
                        }}>
                            PARTNER
                        </div>
                    </div>
                )}

                {/* Heliolab logo - hidden on mobile */}
                {!isAnonMode && !isMobileDevice && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <a
                            href="https://fdl.ai/heliolab"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <img
                                src="/logos/logo-heliolab-text.png"
                                alt="Heliolab"
                                style={{
                                    height: 52,
                                    width: 'auto',
                                    objectFit: "contain",
                                    filter: 'brightness(0.9)',
                                    transition: 'filter 0.2s ease'
                                }}
                            />
                        </a>
                    </div>
                )}

                {/* Copyable URL container */}
                <div style={{ display: 'flex', alignItems: 'center', background: '#111', borderRadius: 6, padding: '6px 12px', maxWidth: '300px' }}>
                    <button
                        onClick={async () => {
                            await handleCopy();
                            setIsCopying(true);
                            setTimeout(() => setIsCopying(false), 1200);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, position: 'relative', minWidth: 22, minHeight: 22 }}
                        title={isCopying ? "Copied!" : "Copy URL"}
                        className={isCopying ? 'icon-collapse-animate' : ''}
                        onAnimationEnd={() => setIsCopying(false)}
                    >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="#fff" strokeWidth="2" fill="none" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="#fff" strokeWidth="2" fill="none" />
                        </svg>

                    </button>
                </div>
            </div>

        </div>
    );
};

export default Navbar; 